// src/api/flightRoutesApi.ts
import { callApi } from '@/utils/api'
import type { ApiMessage, ApiMessageIds } from '@/types/apiResponse'

/** Swaggerの flight_routes GET のレスポンス形 */
export type FlightRoutePoint = {
  point_name: string
  point_pass_time: string
  point_port_cd: number
}

export type FlightRoute = {
  flight_route_id: number
  flight_area_id: number
  flight_area_name: string
  flight_route_name: string
  points: FlightRoutePoint[]
}

export type FlightRoutesResponse = {
  flight_routes?: FlightRoute[]
}

// 航路地点（詳細）
export type FlightRouteDetailPoint = {
  point_name: string
  point_no: number
  point_height: number
  point_passing_speed: number
  point_coordinate: {
    latitude: number
    longitude: number
  }
  route_point_profile_flg: number
  upper_point_profile_flg: number
  point_port_cd?: number
}

// 航路詳細
export type FlightRouteDetail = {
  flight_route_id: number
  flight_area_id: number
  flight_route_name: string
  comment: string
  points: FlightRouteDetailPoint[]
}

// 航路更新（PUT）用 payload
export type UpdateFlightRouteRequest = {
  flight_route_name?: string
  comment?: string
  points?: {
    point_name: string
    point_no: number
    point_height: number
    point_passing_speed: number
    point_coordinate: {
      latitude: number
      longitude: number
    }
    route_point_profile_flg: number
    upper_point_profile_flg: number
    point_port_cd?: number
  }[]
}

/** CSV登録POSTのレスポンス（200: message / 422: message_ids） */
export type RegisterFlightRoutesResponse = ApiMessage & Partial<ApiMessageIds>

/**
 * 航路一覧取得
 * GET /flight-routes?flight_area_id=xx
 */
export async function fetchFlightRoutes(flightAreaId: number): Promise<FlightRoute[]> {
  const res = await callApi<FlightRoutesResponse, { flight_area_id: number }>(
    '/flight-routes',
    'GET',
    { flight_area_id: flightAreaId },
  )
  return res.flight_routes ?? []
}

/**
 * CSVから航路登録
 * POST /flight-routes (multipart/form-data)
 */
export async function registerFlightRoutesByCsv(
  flightAreaId: number,
  csvFile: File,
): Promise<RegisterFlightRoutesResponse> {
  const form = new FormData()
  form.append('flight_area_id', String(flightAreaId))
  form.append('csv_file', csvFile)

  // callApiにFormDataを渡せばOK（Content-Typeはaxiosがboundary付きで付けるので自前で付けない）
  return await callApi<RegisterFlightRoutesResponse, FormData>('/flight-routes', 'POST', form)
}

/**
 * 航路情報の詳細取得
 * GET /flight-routes/{flight_route_id}
 */
export async function fetchFlightRoute(flightRouteId: number): Promise<FlightRouteDetail> {
  const res = await callApi<FlightRouteDetail>(`/flight-routes/${flightRouteId}`, 'GET')
  return res
}

/**
 * 航路情報を更新
 * PUT /flight-routes/{flight_route_id}
 */
export async function updateFlightRoute(
  flightRouteId: number,
  payload: UpdateFlightRouteRequest,
): Promise<ApiMessage & Partial<ApiMessageIds>> {
  return await callApi<ApiMessage & Partial<ApiMessageIds>, UpdateFlightRouteRequest>(
    `/flight-routes/${flightRouteId}`,
    'PUT',
    payload,
  )
}

/**
 * 航路情報を削除
 * DELETE /flight-routes/{flight_route_id}
 */
export async function deleteFlightRoute(
  flightRouteId: number,
): Promise<ApiMessage & Partial<ApiMessageIds>> {
  return await callApi<ApiMessage & Partial<ApiMessageIds>>(
    `/flight-routes/${flightRouteId}`,
    'DELETE',
  )
}
